import { type Signal, useComputed } from "@suse-o11y/signal";
import { Tabs } from "antd/es";
import groupBy from "lodash/groupBy";
import React, { useMemo } from "react";

import { IconChevronDown, IconMainMenuMonitors } from "~/components/icon";
import { Permission } from "~/models/dto";
import { cx } from "~/tools/class-variance";
import type { Record } from "~/tools/record";

import { Api } from "../../common/openapi";
import { usePermissions } from "../../common/use-permissions";
import { MonitorContextMenu } from "../../monitors/MonitorContextMenu";
import { queryMonitorStatus, useInvalidateMonitorStatusQuery, useUpdateMonitorStatus } from "../../queries";
import { useQuery } from "../../queries/react-query";
import { useErrorMessage } from "../_notification-error";
import { Button, ButtonGroup } from "../react/buttons/Button";
import { CloseDrawerButton, DrawerTitle, ExplorationDrawer } from "../react/drawer/ExplorationDrawer";
import { GenericErrorMessage } from "../react/error/GenericErrorMessage";
import { LoadingSpinner } from "../react/loading-spinner/LoadingSpinner";
import { notification } from "../react/notification/Notification";
import { RelativeContainer } from "../react/RelativeContainer";

import { DefinitionTabContent } from "./DefinitionTabContent";
import { IssuesTabContent } from "./IssueTabContent";
import { UnmappedComponentsTabContent } from "./UnmappedComponentsTabContent";

type MonitorPreviewTab = "yaml" | "errors" | "warnings" | "unmapped";

export interface MonitorPreviewDrawerState {
	title: string;
	monitorId: number;
	activeTab: Signal<MonitorPreviewTab>;
}

export const MonitorPreviewDrawer: React.FC<{
	when: undefined | MonitorPreviewDrawerState;
}> = ({ when }) => (
	<ExplorationDrawer
		data-testid="monitor-preview-drawer"
		footer={({ monitorId }) => <MonitorPreviewDrawerFooter monitorId={monitorId} />}
		headerTitle={({ title }) => (
			<DrawerTitle icon={<IconMainMenuMonitors className="text-grey-800" height={20} width={20} />} title={title} />
		)}
		when={when}
	>
		{(state) => <MonitorPreviewDrawerBody {...state} />}
	</ExplorationDrawer>
);

const MonitorPreviewDrawerFooter: React.FC<{
	monitorId: number;
}> = ({ monitorId }) => {
	const permissions = usePermissions();
	const hasCreateMonitorsPermission = permissions?.hasSystemPermission(Permission.CreateMonitors);
	const hasUpdateMonitorsPermission = permissions?.hasSystemPermission(Permission.UpdateMonitors);

	const { displayErrorMessage } = useErrorMessage();

	const { data, isLoading } = useQuery(queryMonitorStatus({ monitorIdOrUrn: monitorId }));
	const invalidateMonitorStatus = useInvalidateMonitorStatusQuery();
	const { isLoading: isUpdating, mutate: updateMonitorStatus } = useUpdateMonitorStatus({
		onSuccess: (result) => {
			notification.success({
				message: `Monitor ${result.name} ${result.status === Api.MonitorStatusValue.ENABLED ? "enabled" : "disabled"}.`,
			});

			return invalidateMonitorStatus();
		},
		onError: (error) => {
			displayErrorMessage({
				message: `Can't change status for monitor ${data?.monitor.name}.`,
				error,
			});
		},
	});

	return (
		<>
			<CloseDrawerButton />
			{isLoading && <LoadingSpinner className="grow-0" size="small" />}
			{data?.monitor && (
				<RelativeContainer>
					<ButtonGroup>
						<Button
							disabled={!hasUpdateMonitorsPermission || isUpdating}
							onClick={() => {
								updateMonitorStatus({
									monitorId: data.monitor.id,
									status:
										data.monitor.status === Api.MonitorStatusValue.ENABLED
											? Api.MonitorStatusValue.DISABLED
											: Api.MonitorStatusValue.ENABLED,
								});
							}}
							type="primary"
						>
							{data.monitor.status === Api.MonitorStatusValue.ENABLED ? "DISABLE" : "ENABLE"} MONITOR
						</Button>
						{(hasCreateMonitorsPermission || hasUpdateMonitorsPermission) && (
							<Button className="p-0" disabled={isUpdating} type="primary">
								<MonitorContextMenu
									icon={
										<div className="h-8 w-[23px]">
											<IconChevronDown className="inline-block align-bottom" />
										</div>
									}
									monitor={data.monitor}
								/>
							</Button>
						)}
					</ButtonGroup>
				</RelativeContainer>
			)}
		</>
	);
};

const MonitorPreviewDrawerBody: React.FC<MonitorPreviewDrawerState> = ({ monitorId, title, activeTab }) => {
	const activeKey = useComputed(activeTab);
	const { data, isLoading, error, refetch } = useQuery(queryMonitorStatus({ monitorIdOrUrn: monitorId }));

	const {
		[Api.MessageLevel.ERROR]: messageLevelErrors = [],
		[Api.MessageLevel.WARN]: messageLevelWarns = [],
	}: Partial<Record<string, Array<Api.MonitorError>>> = useMemo(
		() => groupBy(data?.errors ?? [], "level"),
		[data?.errors],
	);

	if (isLoading) {
		return <LoadingSpinner className="h-full" size="large" />;
	}

	if (error != null) {
		return (
			<GenericErrorMessage
				className="h-full"
				error={error}
				highlightText="Failed"
				onRetry={() => void refetch()}
				text={`Failed to load monitor status for '${title}`}
			/>
		);
	}

	return (
		<Tabs
			activeKey={activeKey}
			className={cx(
				"h-full text-grey-800",
				"[&_.ant-tabs-nav]:before:border-none",
				"[&_.ant-tabs-tab]:text-13/[15px]",
				"[&_.ant-tabs-content]:h-full [&_.ant-tabs-content]:overflow-y-auto",
			)}
			onChange={(tab: MonitorPreviewTab) => {
				activeTab.write(tab);
			}}
			tabBarGutter={24}
			tabBarStyle={{
				margin: 0,
				marginBottom: 20,
			}}
		>
			<Tabs.TabPane key="yaml" tab="Monitor definition">
				<DefinitionTabContent className="h-full" monitorId={monitorId} title={title} />
			</Tabs.TabPane>
			{messageLevelErrors.length > 0 && (
				<Tabs.TabPane key="errors" tab={`Errors (${messageLevelErrors.length})`}>
					<IssuesTabContent errors={messageLevelErrors} level={Api.MessageLevel.ERROR} />
				</Tabs.TabPane>
			)}
			{messageLevelWarns.length > 0 && (
				<Tabs.TabPane key="warnings" tab={`Warnings (${messageLevelWarns.length})`}>
					<IssuesTabContent errors={messageLevelWarns} level={Api.MessageLevel.WARN} />
				</Tabs.TabPane>
			)}
			{data?.topologyMatchResult != null && data.topologyMatchResult.unmatchedCheckStates.length > 0 && (
				<Tabs.TabPane
					key="unmapped"
					tab={`Unmapped identifiers (${data.topologyMatchResult.unmatchedCheckStates.length})`}
				>
					<UnmappedComponentsTabContent unmatchedCheckStates={data.topologyMatchResult.unmatchedCheckStates} />
				</Tabs.TabPane>
			)}
		</Tabs>
	);
};
